import { useTranslations } from '@vocab/react';
import { Text, Box, Stack, Heading } from 'braid-design-system';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { ColumnCard } from 'src/components/Card/ColumnCard';
import { Carousel } from 'src/components/Carousel/Carousel';
import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { SUGGESTIONS } from 'src/components/Router';

import { ProgressBar } from '../Tips/ProgressBar/ProgressBar';

import translations from './.vocab';
import { Suggestion } from './Suggestion/Suggestion';
import { EndStateIcon } from './Suggestion/svg/endStateIcon';
import { profileSuggestionsLoaded } from './actionCreators';
import { useSuggestions } from './data';

import * as styles from './Suggestions.css';

export const Suggestions = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const {
    suggestions,
    loading: suggestionsLoading,
    error: suggestionsError,
  } = useSuggestions();

  const score = suggestions.length / 17;
  const progress = 1 - score;

  const suggestionsComplete = !suggestionsLoading && suggestions.length === 0;

  const showLoading = Boolean(suggestionsLoading || suggestionsError);

  useEffect(() => {
    if (!showLoading && suggestions) {
      const commaSeperatedNudgeTypes = suggestions
        .map((suggestion) => suggestion.trackingNudgeType)
        .join(', ');
      dispatch(
        profileSuggestionsLoaded({
          currentPage: 'profile',
          nudgeType: commaSeperatedNudgeTypes,
          nudgeCount: suggestions.length,
          callToActionType: 'nudge',
        }),
      );
    }
  }, [showLoading, suggestions, dispatch]);

  return (
    <Box
      id={SUGGESTIONS}
      paddingTop="xlarge"
      className={styles.sticky}
      display="flex"
      flexDirection="column"
      data={{
        automation: `suggestions${showLoading ? '-loading-card' : ''}`,
      }}
    >
      <ColumnCard
        name={SUGGESTIONS}
        title={
          <Box paddingLeft="xxsmall">
            <Heading
              level="3"
              component="h2"
              data={{ automation: 'profile-suggestions-heading' }}
            >
              {t('Profile strength')}
            </Heading>
          </Box>
        }
        loading={showLoading}
        loader={
          <Stack space="gutter">
            <LoadingItem type="heading" level="1" />
            <LoadingItem type="text" size="standard" width="full" />
            <LoadingItem type="text" size="standard" width="small" />
            <LoadingItem type="button" />
          </Stack>
        }
      >
        <Stack space="gutter">
          <Box paddingX="xxsmall" marginTop="xxsmall">
            <ProgressBar progress={progress} labelId="profileStrengthLabel" />
          </Box>
          {suggestionsComplete ? (
            <Box
              background="neutralSoft"
              borderRadius="large"
              padding="gutter"
              marginRight="xxsmall"
            >
              <Stack space="large">
                <EndStateIcon />

                <Text data={{ automation: `suggestion-text-endstate` }}>
                  {t('Excellent, your profile is now up to date.')}
                </Text>
              </Stack>
            </Box>
          ) : (
            <Carousel
              items={suggestions}
              component={Suggestion}
              nextButton="icon"
              prevButton="icon"
            />
          )}
        </Stack>
      </ColumnCard>
    </Box>
  );
};
